import { isSampleTestRow } from "./sampleDataFilter";
import { isEmptyValue, normalizeSheetRows } from "./payloadRows";

const sanitizeTherapistCode = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  return raw.replace(/[^a-zA-Z0-9]/g, "");
};

const normalizePhoneDetail = (value: unknown): string => {
  if (isEmptyValue(value)) {
    return "";
  }
  return String(value).replace(/\D/g, "");
};

const normalizeContactLocation = (value: unknown): string => {
  if (isEmptyValue(value)) {
    return "0";
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return "0";
  }

  const parsed = Number(trimmed);
  if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
    return String(Number.isInteger(parsed) ? parsed : Math.trunc(parsed));
  }

  return "0";
};

/** One TherapistContact row per phone or email on each therapist. */
export const mapTherapistContactRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  const therapists = normalizeSheetRows(sheetData);
  const rows: Record<string, unknown>[] = [];

  for (const therapist of therapists) {
    if (isSampleTestRow(therapist)) {
      continue;
    }

    const staffCode = sanitizeTherapistCode(therapist["Staff Code"]);
    if (!staffCode) {
      continue;
    }

    const phoneNumber = normalizePhoneDetail(therapist["Phone Number"]);
    if (phoneNumber) {
      rows.push({
        "Staff Code": staffCode,
        "Contact Type":
          String(therapist["Phone Type"] ?? "Mobile").trim() || "Mobile",
        "Contact Location": normalizeContactLocation(therapist["Country Code"]),
        "Contact Detail": phoneNumber,
      });
    }

    const emailAddress = therapist["Email Address"];
    if (!isEmptyValue(emailAddress)) {
      rows.push({
        "Staff Code": staffCode,
        "Contact Type":
          String(therapist["Email Type"] ?? "Personal").trim() || "Personal",
        "Contact Location": "0",
        "Contact Detail": String(emailAddress).trim(),
      });
    }
  }

  return rows;
};

/** Ensures Staff Code is alphanumeric for the main Therapist worksheet. */
export const mapTherapistMainRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  for (const row of normalizeSheetRows(sheetData)) {
    if (isSampleTestRow(row)) {
      continue;
    }

    const staffCode = sanitizeTherapistCode(row["Staff Code"]);
    if (!staffCode) {
      continue;
    }

    rows.push({
      ...row,
      "Staff Code": staffCode,
      "Seniority Level":
        row["Seniority Level"] === "" || row["Seniority Level"] == null
          ? 0
          : row["Seniority Level"],
      "Hourly Rate":
        row["Hourly Rate"] === "" || row["Hourly Rate"] == null
          ? "0.00"
          : row["Hourly Rate"],
      "Commission Class":
        row["Commission Class"] === "" || row["Commission Class"] == null
          ? 0
          : row["Commission Class"],
    });
  }

  return rows;
};
